import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api.js';
import { useMe } from '../auth.js';
import { Button, ErrorText, Input } from '../components/ui.js';
import type { Partner } from './PartnersPage.js';

interface Agreement {
  id: string;
  partnerId: string;
  percent: number;
  /** null = since the beginning */
  validFrom: string | null;
  validTo: string | null;
}
interface Product {
  id: string;
  kind: string;
  steamId: number;
  name: string;
  gameId: string | null;
}
interface Game {
  id: string;
  name: string;
  steamAppId: number | null;
  autoCreatedAt: string | null;
  sharesCheckedAt: string | null;
  products: Product[];
  agreements: Agreement[];
}

interface MappingTask extends Product {
  months: string[];
  appId: number | null;
  suggestedName: string;
  matchingGame: { id: string; name: string } | null;
}

interface CatalogRun {
  ranAt: string;
  ok: boolean;
  partnerList: 'used' | 'empty' | 'not_configured' | 'failed';
  partnerListError: string | null;
  created: Array<{ name: string; appId: number }>;
  unmapped: Array<{ kind: string; steamId: number; name: string; reason: string }>;
  storeErrors: Array<{ appId: number; error: string }>;
  pendingApps: number[];
}
interface CatalogStatus {
  publisherKey: { hint: string } | null;
  lastRun: CatalogRun | null;
  jobs: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);
const errorText = (e: unknown) => (e instanceof ApiError ? e.message : 'Request failed');
/** The day to offer instead when a share would reach into settled months (409 month_already_settled). */
const firstUnsettledDay = (e: unknown) =>
  e instanceof ApiError && e.status === 409 && e.code === 'month_already_settled' ? ((e.body as { firstUnsettledDay?: string } | undefined)?.firstUnsettledDay ?? null) : null;
const periodLabel = (a: { validFrom: string | null; validTo: string | null }) =>
  a.validFrom && a.validTo ? `${a.validFrom} - ${a.validTo}` : a.validFrom ? `from ${a.validFrom}` : a.validTo ? `until ${a.validTo}` : 'always';

/** Current partner shares of a game; the house keeps the remainder. */
function currentShares(game: Game, partners: Partner[]) {
  const day = today();
  const active = game.agreements.filter((a) => (a.validFrom === null || a.validFrom <= day) && (a.validTo ?? '9999-12-31') >= day);
  const name = (id: string) => partners.find((p) => p.id === id)?.name ?? id;
  const house = Math.round((100 - active.reduce((s, a) => s + a.percent, 0)) * 100) / 100;
  return [...active.map((a) => `${name(a.partnerId)} ${a.percent}%`), `SilentFuture ${house}%`].join(', ');
}

export function GamesPage() {
  const isAdmin = useMe().user?.role === 'admin';
  const client = useQueryClient();
  const games = useQuery({ queryKey: ['games'], queryFn: () => api<Game[]>('GET', '/api/games') });
  const partners = useQuery({ queryKey: ['partners'], queryFn: () => api<Partner[]>('GET', '/api/partners') });
  const tasks = useQuery({ queryKey: ['mapping-tasks'], queryFn: () => api<MappingTask[]>('GET', '/api/mapping-tasks') });
  const catalog = useQuery({ queryKey: ['game-catalog'], queryFn: () => api<CatalogStatus>('GET', '/api/games/catalog'), refetchInterval: 30_000 });
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState<string | null>(null);
  const reload = () => Promise.all(['games', 'mapping-tasks', 'game-catalog'].map((k) => client.invalidateQueries({ queryKey: [k] })));
  const syncGames = useMutation({ mutationFn: () => api<{ queued: boolean }>('POST', '/api/games/catalog/sync'), onSuccess: reload });
  const checked = useMutation({ mutationFn: (gameId: string) => api('POST', `/api/games/${gameId}/shares-checked`), onSuccess: reload });
  const run = catalog.data?.lastRun;
  const withoutApp = run?.unmapped.filter((u) => u.reason === 'no_app_id') ?? [];
  const [name, setName] = useState('');
  const create = useMutation({ mutationFn: () => api('POST', '/api/games', { name }), onSuccess: async () => { setName(''); await reload(); } });
  const mapProduct = useMutation({
    mutationFn: ({ productId, gameId }: { productId: string; gameId: string | null }) => api('PATCH', `/api/products/${productId}`, { gameId }),
    onSuccess: reload,
  });

  return (
    <section className="space-y-6">
      <h1 className="text-xl font-semibold">Games and revenue shares</h1>
      <div className="space-y-1 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-slate-500">
            Game catalog from Steam: {run ? `last run ${new Date(run.ranAt).toLocaleString('en-GB')}${run.ok ? '' : ' (with errors, retried next run)'}` : 'not run yet'}
            {run && run.created.length > 0 && `; created ${run.created.map((g) => g.name).join(', ')}`}
          </span>
          {isAdmin && (
            <Button onClick={() => syncGames.mutate()} disabled={syncGames.isPending || catalog.data?.jobs === false}>Sync games from Steam</Button>
          )}
          {syncGames.data && !syncGames.data.queued && <span className="text-slate-500">A catalog run is already queued.</span>}
        </div>
        {run && (run.storeErrors.length > 0 || run.partnerListError) && (
          <p className="text-red-700 dark:text-red-400">
            {[run.partnerListError, ...run.storeErrors.map((e) => `store app ${e.appId}: ${e.error}`)].filter(Boolean).join('; ')}
          </p>
        )}
        {catalog.data && !catalog.data.publisherKey && (
          <p className="text-slate-500">
            No Steam publisher key: the catalog knows only apps that appear in sales data or on the store. Apps without sales and without a
            store page (unreleased or delisted games) are found only with the publisher key (Steam sync page).
            {withoutApp.length > 0 && ` Products Steam names no app for: ${withoutApp.map((u) => `${u.name} (${u.kind} ${u.steamId})`).join(', ')}.`}
          </p>
        )}
        {catalog.data?.publisherKey && run?.partnerList === 'empty' && (
          <p className="text-amber-700 dark:text-amber-400">The publisher key group has no apps assigned; the partner app list is empty.</p>
        )}
      </div>
      {syncGames.isError && <ErrorText>{errorText(syncGames.error)}</ErrorText>}
      {tasks.data && tasks.data.length > 0 && (
        <div className="rounded border border-amber-400 bg-amber-50 p-4 text-sm dark:bg-amber-950">
          <h2 className="mb-2 font-semibold">Unmapped Steam products ({tasks.data.length}) - months containing them cannot be settled</h2>
          <ul className="space-y-1">
            {tasks.data.map((t) => (
              <li key={t.id} className="space-y-1">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="flex-1">
                    {t.name}{' '}
                    <span className="text-slate-500">({t.kind} {t.steamId}{t.appId ? `, app ${t.appId}` : ''}; months {t.months.join(', ') || 'none yet'})</span>
                  </span>
                  {isAdmin && t.matchingGame && (
                    <Button onClick={() => mapProduct.mutate({ productId: t.id, gameId: t.matchingGame!.id })} disabled={mapProduct.isPending}>Assign to {t.matchingGame.name}</Button>
                  )}
                  {isAdmin && (
                    <select aria-label={`Game for ${t.name}`} className="rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-700 dark:bg-slate-900" defaultValue="" onChange={(e) => e.target.value && mapProduct.mutate({ productId: t.id, gameId: e.target.value })}>
                      <option value="">assign to game ...</option>
                      {games.data?.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  )}
                  {isAdmin && !t.matchingGame && (
                    <button className="underline" onClick={() => setCreating(creating === t.id ? null : t.id)}>{creating === t.id ? 'cancel' : 'Create game'}</button>
                  )}
                </div>
                {isAdmin && creating === t.id && (
                  <CreateGameForm task={t} onDone={async () => { setCreating(null); await reload(); }} onAssign={(gameId) => mapProduct.mutate({ productId: t.id, gameId })} />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {mapProduct.isError && <ErrorText>{errorText(mapProduct.error)}</ErrorText>}
      <table className="w-full text-left text-sm">
        <thead className="text-slate-500"><tr><th className="py-1">Game</th><th>Products</th><th>Current shares</th><th /></tr></thead>
        <tbody>
          {games.data?.map((g) => (
            <GameRow key={g.id} game={g} partners={partners.data ?? []} isAdmin={isAdmin} open={open === g.id} onToggle={() => setOpen(open === g.id ? null : g.id)} onChanged={reload} onSharesChecked={() => checked.mutate(g.id)} />
          ))}
        </tbody>
      </table>
      {isAdmin && (
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }} className="flex max-w-md items-end gap-2">
          <div className="flex-1"><Input label="New game" required value={name} onChange={(e) => setName(e.target.value)} /></div>
          <Button type="submit" disabled={create.isPending}>Add</Button>
        </form>
      )}
      {create.isError && <ErrorText>{errorText(create.error)}</ErrorText>}
      {checked.isError && <ErrorText>{errorText(checked.error)}</ErrorText>}
    </section>
  );
}

/** Creates a game for an unmapped product and assigns it in one step; an existing game is offered instead (409). */
function CreateGameForm({ task, onDone, onAssign }: { task: MappingTask; onDone: () => Promise<unknown>; onAssign: (gameId: string) => void }) {
  const [form, setForm] = useState({ name: task.suggestedName, appId: task.appId ? String(task.appId) : '' });
  const create = useMutation({
    mutationFn: () => api('POST', `/api/products/${task.id}/game`, { name: form.name, steamAppId: form.appId ? Number(form.appId) : null }),
    onSuccess: onDone,
  });
  const existing = create.error instanceof ApiError && create.error.status === 409 ? (create.error.body as { gameId?: string; gameName?: string } | undefined) : undefined;
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }} className="flex flex-wrap items-end gap-2 pl-4">
      <div className="w-72"><Input label="Game name" required maxLength={200} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
      <div className="w-36"><Input label="Steam app id" type="number" min="1" step="1" value={form.appId} onChange={(e) => setForm({ ...form, appId: e.target.value })} /></div>
      <Button type="submit" disabled={create.isPending}>Create and assign</Button>
      {existing?.gameId && <Button type="button" onClick={() => onAssign(existing.gameId!)}>Assign to {existing.gameName}</Button>}
      {create.isError && <ErrorText>{errorText(create.error)}</ErrorText>}
    </form>
  );
}

function GameRow({ game, partners, isAdmin, open, onToggle, onChanged, onSharesChecked }: { game: Game; partners: Partner[]; isAdmin: boolean; open: boolean; onToggle: () => void; onChanged: () => Promise<unknown>; onSharesChecked: () => void }) {
  const isNew = game.autoCreatedAt !== null && game.sharesCheckedAt === null;
  const shareHolders = partners.filter((p) => !p.isHouse);
  const [form, setForm] = useState({ partnerId: '', percent: '', limit: false, from: '', to: '', end: false, endFrom: today() });
  const change = useMutation({
    mutationFn: (overrideFrom?: string) =>
      api('POST', `/api/games/${game.id}/shares`, form.end
        ? { partnerId: form.partnerId, percent: null, from: form.endFrom }
        : { partnerId: form.partnerId, percent: Number(form.percent), from: overrideFrom ?? (form.limit && form.from ? form.from : null), to: form.limit && form.to ? form.to : null }),
    onSuccess: onChanged,
  });
  const offer = firstUnsettledDay(change.error);
  return (
    <>
      <tr className="border-t border-slate-200 align-top dark:border-slate-800">
        <td className="py-1.5 font-medium">
          {game.name}
          {isNew && (
            <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-normal text-amber-900 dark:bg-amber-900 dark:text-amber-100" title="Created automatically from Steam data; 100% SilentFuture until a partner share is set">
              new, check shares
            </span>
          )}
          {isNew && isAdmin && <button className="ml-2 text-xs font-normal underline" onClick={onSharesChecked}>shares are right</button>}
        </td>
        <td>{game.products.map((p) => <span key={p.id} className="block text-xs text-slate-500">{p.name}</span>)}</td>
        <td>{currentShares(game, partners)}</td>
        <td className="text-right">
          {isAdmin ? (
            <Button onClick={onToggle}>{open ? 'Close' : 'Edit shares'}</Button>
          ) : (
            <button className="underline" onClick={onToggle}>{open ? 'close' : 'share history'}</button>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4} className="bg-slate-100 p-3 dark:bg-slate-900">
            <table className="mb-3 text-sm">
              <thead className="text-slate-500"><tr><th className="pr-6">Partner</th><th className="pr-6">Share</th><th className="pr-6">Period</th>{isAdmin && <th />}</tr></thead>
              <tbody>
                {game.agreements.length === 0 && <tr><td colSpan={4}>No partner shares - 100% SilentFuture.</td></tr>}
                {game.agreements.map((a) => (
                  <AgreementRow key={a.id} gameId={game.id} agreement={a} partners={partners} isAdmin={isAdmin} onChanged={onChanged} />
                ))}
              </tbody>
            </table>
            {isAdmin && (
              <form onSubmit={(e) => { e.preventDefault(); change.mutate(undefined); }} className="flex flex-wrap items-end gap-2 text-sm" aria-label="Add or change share from a date">
                <span className="w-full font-medium">Add or change share from a date</span>
                <label>
                  <span className="mb-1 block text-slate-600 dark:text-slate-400">Partner</span>
                  <select required className="rounded border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900" value={form.partnerId} onChange={(e) => setForm({ ...form, partnerId: e.target.value })}>
                    <option value="">choose ...</option>
                    {shareHolders.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
                {!form.end && <div className="w-28"><Input label="Share %" type="number" step="0.01" min="0.01" max="100" required value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} /></div>}
                {!form.end && <label className="flex items-center gap-1 pb-2"><input type="checkbox" checked={form.limit} onChange={(e) => setForm({ ...form, limit: e.target.checked })} /> Limit to a period</label>}
                {!form.end && form.limit && <div className="w-40"><Input label="From (optional)" type="date" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} /></div>}
                {!form.end && form.limit && <div className="w-40"><Input label="Until (optional)" type="date" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} /></div>}
                {form.end && <div className="w-40"><Input label="End from" type="date" required value={form.endFrom} onChange={(e) => setForm({ ...form, endFrom: e.target.value })} /></div>}
                <label className="flex items-center gap-1 pb-2"><input type="checkbox" checked={form.end} onChange={(e) => setForm({ ...form, end: e.target.checked })} /> end share</label>
                <Button type="submit" disabled={change.isPending}>Apply</Button>
                {!form.end && !form.limit && <span className="w-full text-slate-500">Without a period the share holds for the whole life of the game ("always").</span>}
                {change.isError && <ErrorText>{errorText(change.error)}</ErrorText>}
                {offer && !form.end && <Button type="button" onClick={() => change.mutate(offer)}>Apply from {offer} instead</Button>}
              </form>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** One agreement period: read-only line with Edit and Delete (admins), inline edit form and an in-page delete confirmation. */
function AgreementRow({ gameId, agreement: a, partners, isAdmin, onChanged }: { gameId: string; agreement: Agreement; partners: Partner[]; isAdmin: boolean; onChanged: () => Promise<unknown> }) {
  const partnerName = (id: string) => partners.find((p) => p.id === id)?.name ?? id;
  const [mode, setMode] = useState<'view' | 'edit' | 'delete'>('view');
  const [form, setForm] = useState({ partnerId: a.partnerId, percent: String(a.percent), limit: a.validFrom !== null || a.validTo !== null, from: a.validFrom ?? '', to: a.validTo ?? '' });
  const done = async () => { setMode('view'); await onChanged(); };
  const save = useMutation({
    mutationFn: (overrideFrom?: string) =>
      api('PATCH', `/api/games/${gameId}/shares/${a.id}`, {
        partnerId: form.partnerId,
        percent: Number(form.percent),
        validFrom: overrideFrom ?? (form.limit && form.from ? form.from : null),
        validTo: form.limit && form.to ? form.to : null,
      }),
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => api('DELETE', `/api/games/${gameId}/shares/${a.id}`), onSuccess: done });
  const cancel = () => { save.reset(); remove.reset(); setMode('view'); };
  const label = `${partnerName(a.partnerId)} ${periodLabel(a)}`;
  const offer = firstUnsettledDay(save.error);
  if (mode === 'edit') {
    return (
      <tr>
        <td colSpan={4} className="py-2">
          <form onSubmit={(e) => { e.preventDefault(); save.mutate(undefined); }} className="flex flex-wrap items-end gap-2" aria-label={`Edit share ${label}`}>
            <label>
              <span className="mb-1 block text-slate-600 dark:text-slate-400">Partner</span>
              <select required className="rounded border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900" value={form.partnerId} onChange={(e) => setForm({ ...form, partnerId: e.target.value })}>
                {partners.filter((p) => !p.isHouse).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <div className="w-28"><Input label="Share %" type="number" step="0.01" min="0.01" max="100" required value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} /></div>
            <label className="flex items-center gap-1 pb-2"><input type="checkbox" checked={form.limit} onChange={(e) => setForm({ ...form, limit: e.target.checked })} /> Limit to a period</label>
            {form.limit && <div className="w-40"><Input label="From (optional)" type="date" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} /></div>}
            {form.limit && <div className="w-40"><Input label="Until (optional)" type="date" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} /></div>}
            <Button type="submit" disabled={save.isPending}>Save</Button>
            <Button type="button" onClick={cancel}>Cancel</Button>
            {save.isError && <ErrorText>{errorText(save.error)}</ErrorText>}
            {offer && <Button type="button" onClick={() => save.mutate(offer)}>Apply from {offer} instead</Button>}
          </form>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td className="pr-6">{partnerName(a.partnerId)}</td><td className="pr-6">{a.percent}%</td><td className="pr-6">{periodLabel(a)}</td>
      {isAdmin && (
        <td className="space-x-2 whitespace-nowrap">
          {mode === 'delete' ? (
            <>
              <span>Delete this period?</span>
              <Button type="button" onClick={() => remove.mutate()} disabled={remove.isPending}>Confirm delete</Button>
              <Button type="button" onClick={cancel}>Cancel</Button>
            </>
          ) : (
            <>
              <Button type="button" onClick={() => setMode('edit')} aria-label={`Edit share ${label}`}>Edit</Button>
              <Button type="button" onClick={() => setMode('delete')} aria-label={`Delete share ${label}`}>Delete</Button>
            </>
          )}
          {remove.isError && <ErrorText>{errorText(remove.error)}</ErrorText>}
        </td>
      )}
    </tr>
  );
}
